import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import GameHeader from "./GameHeader";
import MobileGameHeader from "./MobileGameHeader";
import MobileMenu from "./MobileMenu";
import MobileHand from "./MobileHand";
import MessageOverlay from "../ui/MessageOverlay";
import EventsColumn from "../layout/EventsColumn";
import StyledSection from "../layout/StyledSection";
import EventsAndIndexes from "../layout/IndexCards";
import GameLogNotification from "../ui/GameLogNotification";
import YourHandRow from "./YourHandRow";
import StocksList from "./StocksList";
import GameTutorial from "../ui/GameTutorial";
import PlayerDetails from "../ui/PlayerDetails";
import ChatOrGameLogPanel from "../ui/ChatOrGameLogPanel";
import GameRulesDialog from "./GameRulesDialog";
import { useGameNotifications } from "../../hooks/useGameNotifications";
import { useGameSocketListeners } from "../../hooks/useGameSocketListeners";
import { useActionCardHandlers } from "../../hooks/useActionCardHandlers";
import { useNotificationDismissal } from "../../hooks/useNotificationDismissal";
import { useTradeHandlers } from "../../hooks/useTradeHandlers";
import { useMobileDetection } from "../../utils/deviceDetection";
import { hasSeenTutorial, markTutorialSeen } from "../../utils/localStorageUtils";

const Game = ({ socket, name, room, setLoggedIn, roundNumber, turnTimeRemaining, turnTimerPlayer, turnTimerTotal, disconnectedPlayers = [], isHost = false }) => {
  const [updates, setUpdates] = useState([]);
  const [myTurn, setMyTurn] = useState(false);
  const [players, setPlayers] = useState([]);
  // add stocks state to show stockcards sent by server
  const [stocks, setStocks] = useState([]);
  // new: track indexes sent from server
  const [indexes, setIndexes] = useState([]);
  // event system
  const [activeEvents, setActiveEvents] = useState([]);
  const [newEventIds, setNewEventIds] = useState(new Set()); // Track newly added events
  const [visualEffects, setVisualEffects] = useState([]); // Combined effects for visual indicators
  const [gameLog, setGameLog] = useState([]);
  const [currentTurn, setCurrentTurn] = useState("");
  const [currentPhase, setCurrentPhase] = useState(null); // Current game phase: "stock_options", "events", "trading"
  const [maxRounds, setMaxRounds] = useState(10); // Maximum number of rounds (default 10, updated from server)
  const [maxActionsPerTurn, setMaxActionsPerTurn] = useState(3); // Maximum actions per turn (default 3, updated from server)
  const [maxActionCards, setMaxActionCards] = useState(6); // Maximum action cards in hand (default 6, updated from server)
  const [tutorialOpen, setTutorialOpen] = useState(false); // Tutorial dialog state
  
  // Mobile state - use touch-based detection
  const isMobile = useMobileDetection();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  
  // Game log notifications hook
  const {
    logNotifications,
    processGameLogUpdate,
    addNotification,
    removeNotification,
    clearAllNotifications,
    initializeFromGameLog
  } = useGameNotifications(name);
  // player cash/wealth
  const [playerCash, setPlayerCash] = useState({});
  // player net worth (cash + stock value)
  const [playerNetWorths, setPlayerNetWorths] = useState({});
  // player portfolios (owned stocks)
  const [playerPortfolios, setPlayerPortfolios] = useState({});
  // player index portfolios (owned indexes)
  const [playerIndexPortfolios, setPlayerIndexPortfolios] = useState({});
  const [actionsRemaining, setActionsRemaining] = useState({}); // Actions left this turn per player: { playerName: count }
  const [maxActionsThisTurn, setMaxActionsThisTurn] = useState({}); // Max actions for current turn per player (2 or 1 for first player when option on)
  // stock ownership counts for price calculation
  const [stockOwnershipCounts, setStockOwnershipCounts] = useState({});
  // stock ownership by player (for showing tokens)
  const [stockOwnershipByPlayer, setStockOwnershipByPlayer] = useState({});
  // index ownership by player (for showing tokens)
  const [indexOwnershipByPlayer, setIndexOwnershipByPlayer] = useState({});
  // player colors and emojis
  const [playerColors, setPlayerColors] = useState({});
  const [playerEmojis, setPlayerEmojis] = useState({});
  // collapsible game log
  const [logExpanded, setLogExpanded] = useState(false);
  // action cards state
  const [playerActionCards, setPlayerActionCards] = useState([]);
  const [newActionCardIds, setNewActionCardIds] = useState(new Set()); // Track newly drawn action cards
  // chat messages for game
  const [chatMessages, setChatMessages] = useState([]);

  // Mobile detection is now handled by useMobileDetection hook

  // Measure header height and set CSS variable for positioning elements below it
  useLayoutEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('.game-header, .game-header-mobile');
      if (header) {
        const height = header.offsetHeight;
        document.documentElement.style.setProperty('--game-header-height', `${height}px`);
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    
    // Use ResizeObserver to watch for header size changes
    const header = document.querySelector('.game-header, .game-header-mobile');
    let resizeObserver;
    if (header && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateHeaderHeight);
      resizeObserver.observe(header);
    }

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [currentPhase, logExpanded, isMobile]); // Re-measure when phase, log expansion, or mobile state changes

  // Auto-show tutorial for new players when trading phase starts
  useEffect(() => {
    if (currentPhase === "trading" && !hasSeenTutorial()) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        setTutorialOpen(true);
        markTutorialSeen(); // Mark as seen so it doesn't show again
      }, 1000); // 1 second delay after trading phase starts
      
      return () => clearTimeout(timer);
    }
  }, [currentPhase]);

  const [snackbars, setSnackbars] = useState([]);
  const nextSnackbarIdRef = useRef(0);
  const hasJoinedGameRef = useRef(false); // Ref version for use in callbacks (setHasJoinedGame is used in socket hook)
  const roomRef = useRef(room); // Ref version for use in callbacks
  const nameRef = useRef(name); // Ref version for use in callbacks
  const playerColorsRef = useRef(playerColors);
  const playerEmojisRef = useRef(playerEmojis);
  
  // Update refs when props change
  useEffect(() => {
    roomRef.current = room;
    nameRef.current = name;
    playerColorsRef.current = playerColors;
    playerEmojisRef.current = playerEmojis;
  }, [room, name, playerColors, playerEmojis]);
  
  const showMessage = useCallback((message, action = null, options = {}) => {
    const id = nextSnackbarIdRef.current;
    nextSnackbarIdRef.current = id + 1;
    setSnackbars(prev => [...prev, { 
      id, 
      message, 
      action, 
      showShare: options.showShare || false,
      shareText: options.shareText || null,
      playerData: options.playerData || null,
      endGameSummary: options.endGameSummary || null
    }]);
  }, []);
  
  const handleSnackbarClose = (id) => {
    const snackbarToClose = snackbars.find(s => s.id === id);
    setSnackbars(prev => prev.filter(s => s.id !== id));
    
    if (snackbarToClose?.action && typeof snackbarToClose.action === "function") {
      snackbarToClose.action();
    }
  };

  // Set card size for stock/index cards (playing card ratio: 2.5" x 3.5" = 0.714)
  const styleCardSize = { width: "150px", height: "210px" };

  // Action card handlers hook (must be after showMessage is defined)
  const {
    selectedActionCard,
    actionMode,
    setSelectedActionCard,
    setActionMode,
    pendingTransferSource,
    setPendingTransferSource,
    handleSelectActionCard,
    executeActionCard,
    handleDrawActionCard,
    redrawMode,
    selectedForDiscard,
    hasUsedRedrawThisTurn,
    handleEnterRedrawMode,
    handleToggleDiscardSelection,
    handleCancelRedraw,
    handleConfirmRedraw
  } = useActionCardHandlers(socket, room, name, actionsRemaining, myTurn, playerActionCards, maxActionCards, showMessage, addNotification);

  // Trade handlers hook
  const {
    handlePurchaseStock,
    handleSellStock,
    handlePurchaseIndex,
    handleSellIndex
  } = useTradeHandlers(socket, room, name, actionsRemaining, selectedActionCard, actionMode, executeActionCard, {
    pendingTransferSource,
    setPendingTransferSource,
    setActionMode,
    setSelectedActionCard,
    stockOwnershipByPlayer,
    addNotification
  });

  // Forced sale handler - called when clicking another player's ownership token
  const handleForcedSale = useCallback((targetPlayerName, stockName) => {
    if (selectedActionCard && actionMode === "selecting_forced_sale") {
      // Execute the action card with target player and stock info
      socket.current.emit("play_action_card", {
        room: room,
        playerName: name,
        cardId: selectedActionCard.id,
        target: { targetPlayerName, stockName }
      });
    }
  }, [socket, room, name, selectedActionCard, actionMode]);

  // Notification dismissal hook
  const { notificationRefs, dismissingRef } = useNotificationDismissal(logNotifications, removeNotification);

  // Use socket listeners hook
  const setHasJoinedGame = useCallback(() => {
    hasJoinedGameRef.current = true;
  }, []);

  useGameSocketListeners(socket, name, room, setLoggedIn, roundNumber, {
    setStocks,
    setIndexes,
    setActiveEvents,
    setNewEventIds,
    setVisualEffects,
    setGameLog,
    setCurrentTurn,
    setCurrentPhase,
    setMaxRounds,
    setMaxActionsPerTurn,
    setMaxActionCards,
    setPlayerCash,
    setPlayerNetWorths,
    setPlayerPortfolios,
    setPlayerIndexPortfolios,
    setActionsRemaining,
    setMaxActionsThisTurn,
    setStockOwnershipCounts,
    setStockOwnershipByPlayer,
    setIndexOwnershipByPlayer,
    setPlayerColors,
    setPlayerEmojis,
    playerColorsRef,
    playerEmojisRef,
    setPlayerActionCards,
    setNewActionCardIds,
    setMyTurn,
    setPlayers,
    setUpdates,
    setHasJoinedGame,
    showMessage,
    addNotification,
    processGameLogUpdate,
    initializeFromGameLog,
    clearAllNotifications,
    hasJoinedGameRef,
    roomRef,
    nameRef,
    notificationRefs,
    dismissingRef,
    setSelectedActionCard,
    setActionMode
  });

  useEffect(() => {
    const myActions = actionsRemaining[name] || 0;
    if (myTurn && myActions === 0) {
      // Add a small delay to let the UI update before ending turn
      const timer = setTimeout(() => {
        setMyTurn(false);
        socket.current.emit("turn_over", { room });
      }, 500); // 500ms delay for smooth UX
      
      return () => clearTimeout(timer);
    }
  }, [actionsRemaining, name, myTurn, room, socket]);

  // Chat socket listeners
  useEffect(() => {
    if (!socket || !socket.current) return;

    const sc = socket.current;
    
    const handleLobbyChatMessage = (message) => {
      setChatMessages(prev => {
        const newMessages = [...prev, message];
        // Keep only last 100 messages to prevent memory issues
        return newMessages.slice(-100);
      });
    };

    const handleLobbyChatError = (error) => {
      showMessage(`❌ Chat error: ${error}`);
    };

    sc.on("lobby_chat_message", handleLobbyChatMessage);
    sc.on("lobby_chat_error", handleLobbyChatError);

    return () => {
      sc.off("lobby_chat_message", handleLobbyChatMessage);
      sc.off("lobby_chat_error", handleLobbyChatError);
    };
  }, [socket, showMessage]);

  const endTurnHandler = () => {
    if (!myTurn) {
      showMessage("It's not your turn!");
      return;
    }
    
    setMyTurn(false);
    // Notify server that turn is over
    socket.current.emit("turn_over", { room });
  };

  const endGame = () => {
    // Emit end game event - server will handle proper cleanup and quit logic
    socket.current.emit("end_game", room);
    // The end_game event handler will show the message and log out
  };




  return (
    <div className="game">
      {/* Game Header - Conditional for Mobile/Desktop */}
      {isMobile ? (
        <MobileGameHeader
          roundNumber={roundNumber}
          maxRounds={maxRounds}
          currentPhase={currentPhase}
          currentTurn={currentTurn}
          name={name}
          playerCash={playerCash}
          playerNetWorths={playerNetWorths}
          playerEmojis={playerEmojis}
          playerColors={playerColors}
          actionsRemaining={actionsRemaining}
          maxActionsPerTurn={maxActionsPerTurn}
          maxActionsThisTurn={maxActionsThisTurn}
          myTurn={myTurn}
          onMenuOpen={() => setMobileMenuOpen(true)}
          onChatOpen={() => setMobileChatOpen(true)}
          turnTimeRemaining={turnTimeRemaining}
          turnTimerPlayer={turnTimerPlayer}
          turnTimerTotal={turnTimerTotal}
        />
      ) : (
        <GameHeader
          roundNumber={roundNumber}
          maxRounds={maxRounds}
          currentTurn={currentTurn}
          currentPhase={currentPhase}
          name={name}
          playerCash={playerCash}
          playerNetWorths={playerNetWorths}
          actionsRemaining={actionsRemaining}
          maxActionsPerTurn={maxActionsPerTurn}
          maxActionsThisTurn={maxActionsThisTurn}
          myTurn={myTurn}
          endTurnHandler={endTurnHandler}
          endGame={endGame}
          onStartTutorial={() => setTutorialOpen(true)}
          turnTimeRemaining={turnTimeRemaining}
          turnTimerPlayer={turnTimerPlayer}
          turnTimerTotal={turnTimerTotal}
        />
      )}

      {/* Mobile Menu Drawer */}
      {isMobile && (
        <MobileMenu
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          players={players}
          currentTurn={currentTurn}
          name={name}
          playerCash={playerCash}
          playerNetWorths={playerNetWorths}
          playerColors={playerColors}
          playerEmojis={playerEmojis}
          actionsRemaining={actionsRemaining}
          maxActionsPerTurn={maxActionsPerTurn}
          maxActionsThisTurn={maxActionsThisTurn}
          currentPhase={currentPhase}
          myTurn={myTurn}
          onEndTurn={endTurnHandler}
          onEndGame={endGame}
          onStartTutorial={() => setTutorialOpen(true)}
          onOpenRules={() => setRulesOpen(true)}
          activeEvents={activeEvents}
          disconnectedPlayers={disconnectedPlayers}
          turnTimeRemaining={turnTimeRemaining}
          turnTimerPlayer={turnTimerPlayer}
          turnTimerTotal={turnTimerTotal}
          isHost={isHost}
          socket={socket}
          room={room}
        />
      )}

      {/* Game Content - Wrapped with background */}
      <div className={isMobile ? "game-content-mobile" : "game-content"}>
        {isMobile ? (
          /* Mobile Layout: 3 Rows (Indexes, Stocks, Events in menu) */
          <div className="mobile-game-rows">
            {/* Mobile Indexes Row */}
            <div className="mobile-indexes-row" data-tutorial="indexes">
              <h3 style={{ 
                fontSize: '1rem', 
                fontWeight: 'bold', 
                marginBottom: '8px',
                color: 'var(--color-text-primary)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                📈 Indexes
              </h3>
              <div className="mobile-indexes-scroll">
                {indexes.length === 0 ? (
                  <div style={{ padding: '20px', color: '#999', fontSize: '0.85rem' }}>
                    No indexes available
                  </div>
                ) : (
                  indexes.map((index) => (
                    <div key={index.name} className="mobile-index-card">
                      <EventsAndIndexes
                        indexes={[index]}
                        styleCardSize={{ width: '100px', height: '140px' }}
                        activeEvents={activeEvents}
                        visualEffects={visualEffects}
                        onPurchaseIndex={handlePurchaseIndex}
                        onSellIndex={handleSellIndex}
                        canPurchaseIndex={myTurn && (actionsRemaining[name] || 0) > 0}
                        canSellIndex={myTurn && (actionsRemaining[name] || 0) > 0}
                        playerIndexPortfolios={playerIndexPortfolios}
                        playerName={name}
                        indexOwnershipByPlayer={indexOwnershipByPlayer}
                        playerColors={playerColors}
                        playerEmojis={playerEmojis}
                        roundNumber={roundNumber}
                        myTurn={myTurn}
                        actionMode={actionMode}
                        selectedActionCard={selectedActionCard}
                        onApplyAction={null}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Mobile Stocks Row */}
            <div className="mobile-stocks-row" data-tutorial="stocks">
              <h3 className="text-base font-bold mb-sm text-primary flex items-center gap-sm">
                💼 Stocks
              </h3>
              <div className="mobile-stocks-scroll">
                {stocks.length === 0 ? (
                  <div className="p-lg text-tertiary text-sm">
                    No stocks available
                  </div>
                ) : (
                  <StocksList
                    stocks={stocks}
                    indexes={indexes}
                    playerPortfolios={playerPortfolios}
                    stockOwnershipCounts={stockOwnershipCounts}
                    stockOwnershipByPlayer={stockOwnershipByPlayer}
                    playerColors={playerColors}
                    playerEmojis={playerEmojis}
                    myTurn={myTurn}
                    actionsRemaining={actionsRemaining}
                    name={name}
                    actionMode={actionMode}
                    selectedActionCard={selectedActionCard}
                    onPurchase={handlePurchaseStock}
                    onSell={handleSellStock}
                    styleCardSize={{ width: '120px', height: '168px' }}
                    onForcedSale={handleForcedSale}
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Desktop Layout: Three Column Layout */
          <div className="game-three-column-layout">
            {/* Column 1: Events and Action Cards (Fixed Left) */}
            <div className="game-column-1">
              <StyledSection
                title="Events and Actions"
                icon="📰"
                isEmpty={false}
                emptyMessage=""
                containerStyle={{
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  flexWrap: "nowrap",
                  flex: 1
                }}
              >
                <EventsColumn
                  activeEvents={activeEvents}
                  newEventIds={newEventIds}
                  setNewEventIds={setNewEventIds}
                  styleCardSize={styleCardSize}
                  onDrawActionCard={handleDrawActionCard}
                  canDrawActionCard={myTurn && (actionsRemaining[name] || 0) > 0}
                  handFull={playerActionCards.length >= maxActionCards}
                />
              </StyledSection>
            </div>

            {/* Column 2: Indexes, Stocks, Your Hand (Center) */}
            <div className="game-column-2">
              {/* Indexes Row */}
              <div className="game-column-2-row">
                <StyledSection
                  title="Indexes"
                  icon="📈"
                  isEmpty={indexes.length === 0}
                  emptyMessage=""
                >
                  <EventsAndIndexes
                    indexes={indexes}
                    styleCardSize={styleCardSize}
                    activeEvents={activeEvents}
                    visualEffects={visualEffects}
                    onPurchaseIndex={handlePurchaseIndex}
                    onSellIndex={handleSellIndex}
                    canPurchaseIndex={myTurn && (actionsRemaining[name] || 0) > 0}
                    canSellIndex={myTurn && (actionsRemaining[name] || 0) > 0}
                    playerIndexPortfolios={playerIndexPortfolios}
                    playerName={name}
                    indexOwnershipByPlayer={indexOwnershipByPlayer}
                    playerColors={playerColors}
                    playerEmojis={playerEmojis}
                    roundNumber={roundNumber}
                    myTurn={myTurn}
                    actionMode={actionMode}
                    selectedActionCard={selectedActionCard}
                    onApplyAction={null}
                  />
                </StyledSection>
              </div>

              {/* Stocks Row */}
              <div className="game-column-2-row">
                <StyledSection
                  title="Stocks"
                  icon="💼"
                  isEmpty={stocks.length === 0}
                  emptyMessage="No stocks available"
                >
                  <StocksList
                    stocks={stocks}
                    indexes={indexes}
                    playerPortfolios={playerPortfolios}
                    stockOwnershipCounts={stockOwnershipCounts}
                    stockOwnershipByPlayer={stockOwnershipByPlayer}
                    playerColors={playerColors}
                    playerEmojis={playerEmojis}
                    myTurn={myTurn}
                    actionsRemaining={actionsRemaining}
                    name={name}
                    actionMode={actionMode}
                    selectedActionCard={selectedActionCard}
                    onPurchase={handlePurchaseStock}
                    onSell={handleSellStock}
                    styleCardSize={styleCardSize}
                    roundNumber={roundNumber}
                    onForcedSale={handleForcedSale}
                  />
                </StyledSection>
              </div>

              {/* Your Hand Row (Fixed) */}
              <YourHandRow
                playerActionCards={playerActionCards}
                newActionCardIds={newActionCardIds}
                setNewActionCardIds={setNewActionCardIds}
                handleSelectActionCard={handleSelectActionCard}
                myTurn={myTurn}
                actionsRemaining={actionsRemaining}
                name={name}
                selectedActionCard={selectedActionCard}
                styleCardSize={styleCardSize}
                maxActionCards={maxActionCards}
                redrawMode={redrawMode}
                selectedForDiscard={selectedForDiscard}
                hasUsedRedrawThisTurn={hasUsedRedrawThisTurn}
                onEnterRedrawMode={handleEnterRedrawMode}
                onToggleDiscardSelection={handleToggleDiscardSelection}
                onCancelRedraw={handleCancelRedraw}
                onConfirmRedraw={handleConfirmRedraw}
              />
            </div>

            {/* Column 3: Player Details and Chat (Fixed Right) */}
            <div className="game-column-3">
              <div className="player-details-wrapper">
                <PlayerDetails
                  players={players}
                  currentTurn={currentTurn}
                  name={name}
                  playerCash={playerCash}
                  playerNetWorths={playerNetWorths}
                  playerColors={playerColors}
                  playerEmojis={playerEmojis}
                  actionsRemaining={actionsRemaining}
                  maxActionsPerTurn={maxActionsPerTurn}
                  maxActionsThisTurn={maxActionsThisTurn}
                  currentPhase={currentPhase}
                  disconnectedPlayers={disconnectedPlayers}
                  turnTimeRemaining={turnTimeRemaining}
                  turnTimerPlayer={turnTimerPlayer}
                  turnTimerTotal={turnTimerTotal}
                  isHost={isHost}
                  socket={socket}
                  room={room}
                />
              </div>
              <div className="game-chat-wrapper">
                <ChatOrGameLogPanel
                  socket={socket}
                  room={room}
                  name={name}
                  playerColors={playerColors}
                  playerEmojis={playerEmojis}
                  messages={chatMessages}
                  gameLog={gameLog}
                  updates={updates}
                />
              </div>
            </div>
          </div>
        )}
      </div>
  
      <MessageOverlay
        snackbars={snackbars}
        handleSnackbarClose={handleSnackbarClose}
      />

      {/* Game Log Notifications - stacked (newest at top) */}
      <div 
        className="game-log-notifications-container"
      >
        {[...logNotifications].reverse().map((notification, index) => (
          <GameLogNotification
            key={notification.id}
            ref={(el) => {
              if (el) {
                notificationRefs.current[notification.id] = el;
              } else {
                delete notificationRefs.current[notification.id];
              }
            }}
            message={notification.message}
            playerName={notification.playerName}
            playerEmoji={notification.playerName ? playerEmojis[notification.playerName] : null}
            playerColor={notification.playerName ? playerColors[notification.playerName] : null}
            onComplete={() => {
              removeNotification(notification.id);
            }}
          />
        ))}
      </div>

      {/* Game Tutorial - Manually triggered */}
      <GameTutorial
        isOpen={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
      />

      {/* Game Rules Dialog */}
      <GameRulesDialog
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
      />

      {/* Mobile Hand (Swipeable) */}
      {isMobile && (
        <MobileHand
          playerActionCards={playerActionCards}
          newActionCardIds={newActionCardIds}
          setNewActionCardIds={setNewActionCardIds}
          handleSelectActionCard={handleSelectActionCard}
          myTurn={myTurn}
          actionsRemaining={actionsRemaining}
          name={name}
          selectedActionCard={selectedActionCard}
          styleCardSize={{ width: '150px', height: '210px' }}
          maxActionCards={maxActionCards}
          onDrawActionCard={handleDrawActionCard}
          canDrawActionCard={myTurn && (actionsRemaining[name] || 0) > 0}
          handFull={playerActionCards.length >= maxActionCards}
          redrawMode={redrawMode}
          selectedForDiscard={selectedForDiscard}
          hasUsedRedrawThisTurn={hasUsedRedrawThisTurn}
          onEnterRedrawMode={handleEnterRedrawMode}
          onToggleDiscardSelection={handleToggleDiscardSelection}
          onCancelRedraw={handleCancelRedraw}
          onConfirmRedraw={handleConfirmRedraw}
        />
      )}

      {/* Mobile Chat Overlay */}
      {isMobile && (
        <div className={`mobile-chat-overlay ${mobileChatOpen ? 'open' : ''}`}>
          <div className="mobile-chat-overlay-content">
            <ChatOrGameLogPanel
              socket={socket}
              room={room}
              name={name}
              playerColors={playerColors}
              playerEmojis={playerEmojis}
              messages={chatMessages}
              gameLog={gameLog}
              updates={updates}
            />
          </div>
        </div>
      )}

      {/* Mobile Chat Backdrop */}
      {isMobile && mobileChatOpen && (
        <div
          className="mobile-chat-backdrop"
          onClick={() => setMobileChatOpen(false)}
        />
      )}
    </div>
  );
};

export default Game;
